import { useCallback, useEffect, useMemo, useState } from 'react';

import { Backdrop, Box, CircularProgress } from '@mui/material';
import { useBlocker } from 'react-router';

import presets from '@/components/Dialog/presets';
import { PageInformationContext } from '@/context/PageInformationContext';
import { RemoveWidgetContext } from '@/context/RemoveWidgetContext';
import { useDeleteDashboard, useSaveDashboard } from '@/features/Dashboard/api/mutations';
import { TAB_DEFAULT_BASE_NAME } from '@/features/Dashboard/constants/names';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import AppsOrder from '@/features/Dashboard/pages/Edit/components/AppsOrder';
import { EditBanner } from '@/features/Dashboard/pages/Edit/components/EditBanner';
import EditDashboardName from '@/features/Dashboard/pages/Edit/components/EditDashboardName';
import { TilesOrder } from '@/features/Dashboard/pages/Edit/components/TilesOrder';
import { buildPreviewConfig } from '@/features/Dashboard/pages/Edit/model/utils/mapping';
import { useMarketPreview } from '@/features/Markets/api';
import { useAppSelector } from '@/hooks/redux-hooks';

import { ConfirmationDialog, EditableTabLabel, EditActionButtons } from './components';
import { useDashboardEditActions } from './hooks/useDashboardEditActions';
import { useDashboardEditInit } from './hooks/useDashboardEditInit';
import {
  selectCurrentMarketTabApps,
  selectCurrentTabIndex,
  selectCurrentTiles,
  selectDraftTabNames,
  selectDraftTabs,
  selectHasChanges,
  selectHasTabNameErrors,
  selectInitialDashboardName
} from './model/dashboardEdit.selectors';
import { DashboardLayout } from '../../components/DashboardLayout';

import type { DashboardTab, MarketApps } from '../../types';

export const EditDashboard = ({
  onClose,
  dashboardName: currentDashboardName,
  initialDashboardName: initialNameProp,
  dashboardConfig,
  activeTab = 0
}: {
  onClose: () => void;
  dashboardName: string;
  initialDashboardName: string;
  dashboardConfig: MarketApps;
  dashboardStructure?: DashboardTab | null;
  activeTab?: number;
}) => {
  // Initialize draft from React Query cache
  const { isInitialized, isLoading: isInitLoading } = useDashboardEditInit({
    dashboardName: currentDashboardName,
    initialDashboardName: initialNameProp,
    dashboardConfig,
    activeTabIndex: activeTab
  });

  // Selectors
  const draftTabs = useAppSelector(selectDraftTabs);
  const draftTabNames = useAppSelector(selectDraftTabNames);
  const hasChanges = useAppSelector(selectHasChanges);
  const hasTabNameErrors = useAppSelector(selectHasTabNameErrors);
  const currentTabIndex = useAppSelector(selectCurrentTabIndex);
  const currentTiles = useAppSelector(selectCurrentTiles);
  const currentMarketTabApps = useAppSelector(selectCurrentMarketTabApps);

  const previewConfig = useMemo(
    () => buildPreviewConfig(currentTiles, currentMarketTabApps),
    [currentTiles, currentMarketTabApps]
  );
  const { data: previewData } = useMarketPreview(previewConfig);

  // Actions
  const { renameTab, addTab, deleteTab, reorderTabs, setCurrentTab, resetToEmpty } =
    useDashboardEditActions();

  // Save/Delete mutations
  const initialDashboardName = useAppSelector(selectInitialDashboardName);
  const { mutate: saveMutate, isPending: isSaving, isSuccess: isSaveSuccess } = useSaveDashboard();
  const { mutate: deleteDashboard, isPending: isDeletingDashboard } =
    useDeleteDashboard(initialDashboardName);

  const { setMode } = useAppForm();

  useEffect(() => {
    setMode('edit');
  }, [setMode]);

  const [isDiscardPopupOpen, setIsDiscardPopupOpen] = useState(false);
  const [isRemoveAllPopupOpen, setIsRemoveAllPopupOpen] = useState(false);
  const [isRemoveDashboardPopupOpen, setIsRemoveDashboardPopupOpen] = useState(false);
  const [isRemoveWidgetPopupOpen, setIsRemoveWidgetPopupOpen] = useState(false);
  const [removeWidgetCallback, setRemoveWidgetCallback] = useState<(() => void) | null>(null);
  const [hasDashboardNameErrors, setHasDashboardNameErrors] = useState(false);

  useEffect(() => {
    if (isSaveSuccess) {
      onClose();
    }
  }, [isSaveSuccess, onClose]);

  const handleRemoveAll = useCallback(() => {
    resetToEmpty();
    setIsRemoveAllPopupOpen(false);
  }, [resetToEmpty]);

  const handleRemoveDashboard = useCallback(() => {
    deleteDashboard();
  }, [deleteDashboard]);

  const openRemoveModal = useCallback((onOk: () => void) => {
    setRemoveWidgetCallback(() => onOk);
    setIsRemoveWidgetPopupOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isSaveDisabled = isSaving || !hasChanges || hasDashboardNameErrors || hasTabNameErrors;

  const handleSave = useCallback(() => {
    saveMutate();
  }, [saveMutate]);

  const tabsConfig = useMemo(() => {
    return draftTabs.map(tab => {
      return {
        id: tab.id,
        name: tab.name,
        label: (
          <EditableTabLabel
            allowTabReorder
            canDelete={draftTabs.length > 1}
            name={tab.name}
            tabId={tab.id}
            onDelete={deleteTab}
            onRename={renameTab}
          />
        )
      };
    });
  }, [draftTabs, deleteTab, renameTab]);

  const handleAddTabDraft = useCallback(() => {
    const tabId = crypto.randomUUID();
    // The slice will automatically generate a unique name using generateUniqueTabName
    addTab(tabId, TAB_DEFAULT_BASE_NAME);
  }, [addTab]);

  const handleDraftTabChange = useCallback(
    (index: number) => {
      const tabId = draftTabs[index]?.id;
      if (tabId) {
        setCurrentTab(tabId);
      }
    },
    [draftTabs, setCurrentTab]
  );

  const handleReorderTabs = useCallback(
    (from: number, to: number) => {
      reorderTabs(from, to);
    },
    [reorderTabs]
  );

  const handleTabCreated = useCallback(
    (tabName: string, tabId: string) => {
      addTab(tabId, tabName);
    },
    [addTab]
  );

  const handleDiscardClick = useCallback(() => {
    if (hasChanges) {
      setIsDiscardPopupOpen(true);
    } else {
      handleClose();
    }
  }, [hasChanges, handleClose]);

  const { setDrawerStep, setIsEditMode, setEditingTarget } = useDrawerSteps();

  const handleAddAppClick = useCallback(() => {
    setIsEditMode(true);
    setEditingTarget(null);
    setDrawerStep(DrawerStep.SelectApp);
  }, [setDrawerStep, setEditingTarget, setIsEditMode]);

  const removeWidgetValue = useMemo(() => {
    return {
      openRemoveModal
    };
  }, [openRemoveModal]);

  const blocker = useBlocker(() => {
    return !isDeletingDashboard && hasChanges && !isSaveSuccess && !isSaving;
  });

  const handleBlockerConfirm = useCallback(() => {
    onClose();
    blocker.proceed?.();
  }, [blocker, onClose]);

  const handleBlockerCancel = useCallback(() => {
    blocker.reset?.();
  }, [blocker]);

  // Show loading state while:
  // 1. Initial data is loading and draft is not initialized
  // 2. Draft is being reinitialized (isInitialized will be false when draft is stale)
  if (!isInitialized || isInitLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const allowTabReorder = draftTabs.length > 1;

  return (
    <RemoveWidgetContext.Provider value={removeWidgetValue}>
      <Backdrop
        open={isSaving || isDeletingDashboard}
        sx={{
          zIndex: 99999999
        }}
      >
        <CircularProgress />
      </Backdrop>
      <ConfirmationDialog
        open={isDiscardPopupOpen}
        {...presets.discard}
        onCancel={() => {
          setIsDiscardPopupOpen(false);
        }}
        onConfirm={() => {
          handleClose();
          setIsDiscardPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveWidgetPopupOpen}
        {...presets.removeWidget}
        onCancel={() => {
          setIsRemoveWidgetPopupOpen(false);
        }}
        onConfirm={() => {
          if (removeWidgetCallback) {
            removeWidgetCallback();
            setRemoveWidgetCallback(null);
          }
          setIsRemoveWidgetPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveAllPopupOpen}
        {...presets.removeAll}
        onCancel={() => {
          setIsRemoveAllPopupOpen(false);
        }}
        onConfirm={() => {
          handleRemoveAll();
          setIsRemoveAllPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={isRemoveDashboardPopupOpen}
        {...presets.removeDashboard}
        onCancel={() => {
          setIsRemoveDashboardPopupOpen(false);
        }}
        onConfirm={() => {
          handleRemoveDashboard();
          setIsRemoveDashboardPopupOpen(false);
        }}
      />
      <ConfirmationDialog
        open={blocker.state === 'blocked'}
        {...presets.discard}
        onCancel={handleBlockerCancel}
        onConfirm={handleBlockerConfirm}
      />
      <DashboardLayout
        disableTabRipple
        activeTab={currentTabIndex}
        minTabWidth={50}
        renderRight={
          <EditActionButtons
            isPending={isSaving || isDeletingDashboard}
            isSaveDisabled={isSaveDisabled}
            onAddApp={handleAddAppClick}
            onDiscard={handleDiscardClick}
            onRemoveAll={() => {
              setIsRemoveAllPopupOpen(true);
            }}
            onRemoveDashboard={() => {
              setIsRemoveDashboardPopupOpen(true);
            }}
            onSave={handleSave}
          />
        }
        tabNames={draftTabNames}
        tabsConfig={tabsConfig}
        title={<EditDashboardName onValidationChange={setHasDashboardNameErrors} />}
        onAddTab={handleAddTabDraft}
        onReorderTabs={allowTabReorder ? handleReorderTabs : undefined}
        onTabChange={handleDraftTabChange}
      >
        <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <EditBanner />
            <TilesOrder preview={previewData} />
            <AppsOrder
              preview={previewData}
              onTabCreated={handleTabCreated}
            />
          </Box>
        </PageInformationContext>
      </DashboardLayout>
    </RemoveWidgetContext.Provider>
  );
};
